import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { IIntegrationUIConfig } from '@feathr/components';
import { Button, CardV2, EmptyState, FieldMappingTable, Skeleton } from '@feathr/components';
import useRaisersEdgeContactMappings from '@feathr/extender/App/Settings/Account/RaisersEdgeIntegrationPage/useRaisersEdgeContactMappings';
import { useLocalUrl } from '@feathr/extender/state';

import { BLACKBAUD_ICON } from '../../blackbaudIcon';
import type { IRaisersEdgeMappingDraft } from '../RaisersEdgeMappingsTab.hooks';
import { useFeathrFieldOptions } from '../RaisersEdgeMappingsTab.hooks';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  config: IIntegrationUIConfig;
  draft: IRaisersEdgeMappingDraft;
}

function RaisersEdgeMappingTable({ config, draft, integration }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  // This table owns the mapping load; the page's draft reads the same store.
  const store = useRaisersEdgeContactMappings(integration);
  const feathrFieldOptions = useFeathrFieldOptions(true);

  function handleRetry(): void {
    void store.fetch();
  }

  if (!store.isFetched && store.error && !store.isPending) {
    return (
      <EmptyState
        description={t('Try again. If the problem continues, reload the page.')}
        label={t("Couldn't load mappings")}
        name={'raisers-edge-mappings-error'}
      >
        <Button onClick={handleRetry} type={'primary'}>
          {t('Try again')}
        </Button>
      </EmptyState>
    );
  }

  if (!store.isFetched || !feathrFieldOptions) {
    return (
      <CardV2 width={'full'}>
        <CardV2.Content>
          <Skeleton paragraphs={4} title={true} />
        </CardV2.Content>
      </CardV2>
    );
  }

  return (
    <FieldMappingTable
      config={config}
      crmIcon={BLACKBAUD_ICON}
      descriptionLink={{
        label: t('Add custom fields'),
        href: localUrl('/data/custom-fields/add'),
      }}
      feathrFieldOptions={feathrFieldOptions}
      mappings={draft.rows}
      // Rows get `sync-mapping-<Blackbaud field key>`, the v1 e2e name.
      name={'sync-mapping'}
      onChange={draft.setRows}
    />
  );
}

export default observer(RaisersEdgeMappingTable);
